import { TableCell, TableRow } from "@/components/ui/table";
import { MetricTooltip } from "./MetricTooltip";
import { URLAnalysis } from "@/types/seo";

interface CompetitiveMetricRowProps {
  label: string;
  description: string;
  analyses: URLAnalysis[];
  getValue: (analysis: URLAnalysis) => number | string | null;
  renderValue?: (value: number | string | null) => React.ReactNode;
  getBestMetric?: (value: number | string | null, bestValue: number) => boolean;
}

export const CompetitiveMetricRow = ({ 
  label, 
  description, 
  analyses,
  getValue,
  renderValue,
  getBestMetric
}: CompetitiveMetricRowProps) => {
  const getBestValue = () => {
    if (!getBestMetric) return -1;
    
    let bestValue = -Infinity;
    let bestIndices: number[] = [];
    
    // First check if all values are 0 for metrics that shouldn't show trophy in that case
    if (label === "PageRank" || label === "Domínios de Referência" || label === "Backlinks") {
      const allZeros = analyses.slice(0, 3).every(analysis => {
        const value = getValue(analysis);
        return value === 0 || value === null || value === undefined;
      });
      if (allZeros) return [];
    }
    
    analyses.slice(0, 3).forEach((analysis, index) => {
      const value = getValue(analysis);
      if (value !== null && value !== undefined) {
        // Special handling for Google position - ignore position 0
        if (label === "Posição no Google" && value === 0) {
          return;
        }

        const numericValue = typeof value === 'number' ? value : 
          typeof value === 'string' ? parseFloat(value) : null;
        
        if (numericValue !== null) {
          if (getBestMetric(numericValue, bestValue)) {
            bestValue = numericValue;
            bestIndices = [index];
          } else if (numericValue === bestValue) {
            bestIndices.push(index);
          }
        }
      }
    });
    return bestIndices;
  };

  const bestIndices = getBestValue();

  // Check if any analysis has this metric
  const hasMetric = analyses.some(analysis => {
    const value = getValue(analysis);
    // Special handling for PageRank and Backlinks - hide if semrush data is not available
    if (label === "PageRank" || label === "Backlinks") {
      return analysis.semrush?.backlinks !== undefined && value !== null && value !== undefined && value !== '';
    }
    return value !== null && value !== undefined && value !== '';
  });
  
  if (!hasMetric) return null;

  const formatNumber = (value: number | string | null) => {
    if (value === null || value === undefined) return '';
    
    if (typeof value === 'number') {
      // Add 'º' symbol if this is the Google position metric
      if (label === "Posição no Google") {
        return `${value.toLocaleString('pt-BR')}º`;
      }
      return value.toLocaleString('pt-BR');
    }
    
    const num = parseFloat(value.toString());
    if (!isNaN(num)) {
      // Add 'º' symbol if this is the Google position metric
      if (label === "Posição no Google") {
        return `${num.toLocaleString('pt-BR')}º`;
      }
      return num.toLocaleString('pt-BR');
    }
    
    return value;
  };

  const getDisplayValue = (value: number | string | null) => {
    if (label === "Título" && (!value || value === '')) {
      return "Título ausente ☠️";
    }
    if (label === "Meta Description" && (!value || value === '')) {
      return "Descrição ausente ☠️";
    }
    return renderValue ? renderValue(value) : formatNumber(value);
  };

  return (
    <TableRow>
      <TableCell className="font-medium flex items-center gap-2">
        {label}
        {label === "PageRank" ? (
          <MetricTooltip 
            label={label} 
            description="Pontuação que indica a autoridade da página, calculada com base em diversos fatores. A métrica vai de 0 a 10, exibimos apenas números inteiros arredondados para cima mas internamente calculamos com precisão decimal." 
          />
        ) : (
          <MetricTooltip label={label} description={description} />
        )}
      </TableCell>
      {analyses.slice(0, 3).map((analysis, index) => {
        const value = getValue(analysis);
        const shouldShowTrophy = Array.isArray(bestIndices) && bestIndices.includes(index) && value !== null && getBestMetric;
        const isPosition = label === "Posição no Google";
        const isZeroPosition = isPosition && value === 0;
        
        return (
          <TableCell key={analysis.url} className="text-center">
            <div className="flex items-center justify-center gap-1">
              {getDisplayValue(value)}
              {shouldShowTrophy && ' 🏆'}
              {isZeroPosition && (
                <span title="A página não aparece entre os 100 primeiros resultados do Google">☠️</span>
              )}
            </div>
          </TableCell>
        );
      })}
    </TableRow>
  );
};